import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useCallback } from 'react';
import { Button, Grid, GridRow, List, ListItem, Modal, ModalBody, ModalHeader, Spacer, Typography } from '@emburse/embark-core';
import { DataGridHeader } from '../../components';
import SingleColumnLayout from '../../components/Layouts/SingleColumnLayout';
import { deleteCompanyPreference, getCompanyPreference, saveCompanyPreference, saveUserPreference } from '../../api/api';
import { getDataInterchangeDirectories, getDataInterchanges } from '../../api/dataInterchange';
import { getCredentialsByCompany } from '../../api/sftp';
import ConfigurationTable from './ConfigurationTable/ConfigurationTable';
import CredentialsTable from './CredentialsTable/CredentialsTable';
import SFTPConfigProvider from './SFTPConfigProvider';
import { $t, useTranslation } from '../../hooks/useTranslation';
const { history } = window;
function SFTPConfig({ UserAgreementSaved, IsCertifyAdmin, CanCreate }) {
    useTranslation();
    /** Modal Props */
    const [termsModalOpen, setTermsModalOpen] = useState(!UserAgreementSaved);
    /** Loading Props */
    const [credentialsLoading, setCredentialsLoading] = useState(true);
    const [sftpPathsLoading, setSFTPPathsLoading] = useState(true);
    /** Page Load */
    const [dataInterchanges, setDataInterchanges] = useState([]);
    const [sftpPaths, setSFTPPaths] = useState([]);
    const [sftpCredentials, setSFTPCredentials] = useState([]);
    const [canCreate, setCanCreate] = useState(CanCreate);
    const setSFTPPathsPageLoading = () => {
        setSFTPPathsLoading(true);
    };
    const transformSFTPCredentials = (row) => {
        const transformedRow = row;
        transformedRow.id = `${transformedRow.hostname}:${transformedRow.username}`;
        return transformedRow;
    };
    const transformSFTPPaths = (row, i) => {
        const transformedRow = row;
        transformedRow.id = i;
        transformedRow.Name = transformedRow.Path;
        transformedRow.Status = 'Unknown';
        return transformedRow;
    };
    const transformInterchangeData = (row) => {
        const transformedRow = row;
        transformedRow.id = transformedRow.PK;
        return transformedRow;
    };
    /** Update Functions for Props */
    const updateSFTPCredentials = useCallback(() => {
        setCredentialsLoading(true);
        getCredentialsByCompany().then((resp) => {
            const data = JSON.parse(resp.Data);
            setSFTPCredentials([...data.map(transformSFTPCredentials)]);
            setCredentialsLoading(false);
        });
    }, []);
    const updateSFTPPaths = () => {
        setSFTPPathsLoading(true);
        getDataInterchangeDirectories(dataInterchanges).then((resp) => {
            setSFTPPaths([...resp.map(transformSFTPPaths)]);
            setSFTPPathsLoading(false);
        });
    };
    useEffect(() => {
        let transformedData;
        getDataInterchanges()
            .then((resp) => {
            const data = JSON.parse(resp.Data);
            transformedData = [...data.map(transformInterchangeData)];
            setDataInterchanges(transformedData);
        })
            .then(() => {
            setSFTPPathsLoading(true);
            getDataInterchangeDirectories(transformedData).then((resp) => {
                setSFTPPaths([...resp.map(transformSFTPPaths)]);
                setSFTPPathsLoading(false);
            });
        });
        getCompanyPreference('EnableSFTPConfig', '').then((resp) => {
            if (resp.Data === '') {
                setCanCreate(false);
            }
            else {
                setCanCreate(true);
            }
        });
        updateSFTPCredentials();
    }, [updateSFTPCredentials]);
    const handleAllowCreationChange = () => {
        if (!canCreate) {
            saveCompanyPreference('EnableSFTPConfig', '', '1');
        }
        else {
            deleteCompanyPreference('EnableSFTPConfig', '');
        }
        setCanCreate(!canCreate);
    };
    const handleIAgreeClick = () => {
        if (!IsCertifyAdmin) {
            const current = new Date();
            saveUserPreference('SFTPConfig_Agreed', `${current.getMonth() + 1}/${current.getDate()}/${current.getFullYear()} ${current.getHours()}:${current.getMinutes()}`).then(() => {
                setTermsModalOpen(false);
            });
        }
        else {
            setTermsModalOpen(false);
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(SingleColumnLayout, { children: !termsModalOpen && (_jsxs(SFTPConfigProvider, { children: [_jsx(DataGridHeader, { title: $t('SFTP Configuration') }), _jsx(CredentialsTable, { loading: credentialsLoading, credentials: sftpCredentials, sftpPaths: sftpPaths, createAllowed: canCreate, isCertifyAdmin: IsCertifyAdmin, updateData: updateSFTPCredentials }), IsCertifyAdmin && (_jsx(ConfigurationTable, { loading: sftpPathsLoading, sftpPaths: sftpPaths, createAllowed: canCreate, updateData: updateSFTPPaths, updateCreateAllow: handleAllowCreationChange, updateLoading: setSFTPPathsPageLoading }))] })) }), _jsx(Modal, { open: termsModalOpen, onClose: () => setTermsModalOpen(false), disableBackdropClick: !IsCertifyAdmin, disableEscapeKeyDown: !IsCertifyAdmin, size: "md", modalHeader: _jsx(ModalHeader, { title: $t('Certify SFTP Site Usage Agreement'), showClose: false }), modalFooter: _jsx("div", { style: { padding: '10px' }, children: _jsxs(GridRow, { spacing: 2, justifyContent: "flex-end", alignItems: "center", children: [_jsx(Grid, { item: true, children: _jsx(Button, { variant: "outlined", color: "primary", onClick: () => {
                                        history.back();
                                    }, children: $t('CANCEL') }) }), _jsx(Grid, { item: true, children: _jsx(Button, { color: "primary", onClick: () => {
                                        handleIAgreeClick();
                                    }, children: $t('I AGREE') }) })] }) }), children: _jsxs(ModalBody, { children: [_jsx(Typography, { variant: "subtitle1", children: $t('Prerequisites') }), _jsxs(List, { disablePadding: true, children: [_jsx(ListItem, { children: $t("You must connect using SFTP software/protocol; HTTPS connections are 'NOT' supported.") }), _jsx(ListItem, { children: $t('You must be familiar with how to use SFTP. Please seek support from your internal IT department for software and/or instructions.') }), _jsx(ListItem, { children: $t(`Do not share account credentials. If you have a 3rd party which requires access to your SFTP account,
                            please contact your Customer Success Manager.`) })] }), _jsx(Spacer, { size: 16 }), _jsx(Typography, { variant: "subtitle1", children: $t('Site Rules') }), _jsxs(List, { disablePadding: true, children: [_jsx(ListItem, { children: $t('Connection attempts are limited to 24 connections a day; SFTP parking (persistent connection) is not permitted.') }), _jsxs(ListItem, { children: [$t('Files containing sensitive data delivered to Certify'), _jsxs("strong", { children: ["\u00A0", $t('MUST'), "\u00A0"] }), $t('be PGP encrypted.')] }), _jsx(ListItem, { children: $t('Files are removed after 30 days.') })] })] }) })] }));
}
export default SFTPConfig;
